import type { AvailabilityConfidence, ChargeFrequency, ListingStatus } from '../api/types';

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const INR_WITH_PAISE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Indian grouping, e.g. ₹12,500. Paise are shown only when they are non-zero. */
export function money(amount: number | null | undefined, options?: { alwaysShowPaise?: boolean }): string {
  if (amount === null || amount === undefined) return '—';

  const hasPaise = Math.round(amount * 100) % 100 !== 0;
  return hasPaise || options?.alwaysShowPaise ? INR_WITH_PAISE.format(amount) : INR.format(amount);
}

/** Compact form for listing cards: ₹12.5K, ₹1.2L. */
export function moneyCompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';

  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(amount % 10000000 === 0 ? 0 : 1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(amount % 100000 === 0 ? 0 : 1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}K`;

  return INR.format(amount);
}

export function priceRange(from?: number, to?: number, unit = 'month'): string {
  const suffix = unit === 'night' ? '/night' : '/month';

  if (from === undefined || from === null) return 'Price on request';
  if (to === undefined || to === null || to === from) return `${money(from)}${suffix}`;

  return `${money(from)} – ${money(to)}${suffix}`;
}

// --- Dates ----------------------------------------------------------------------------

const IST = 'Asia/Kolkata';
const unavailableDate = 'Date unavailable';

function parseTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Every timestamp from the API is UTC. Conversion to India Standard Time happens here,
 * at display time, and nowhere else.
 */
export function dateTime(utc: string | null | undefined): string {
  const parsed = parseTimestamp(utc);
  if (!parsed) return utc ? unavailableDate : '—';

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: IST,
  }).format(parsed);
}

export function date(utc: string | null | undefined): string {
  const parsed = parseTimestamp(utc);
  if (!parsed) return utc ? unavailableDate : '—';

  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeZone: IST }).format(parsed);
}

/** A DateOnly value from the API, which carries no time zone at all. */
export function plainDate(value: string | null | undefined): string {
  if (!value) return '—';

  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(year, month - 1, day));
}

export function relativeTime(utc: string | null | undefined): string {
  if (!utc) return 'never';

  const parsed = parseTimestamp(utc);
  if (!parsed) return 'time unavailable';

  const diffMs = Date.now() - parsed.getTime();
  const minutes = Math.round(diffMs / 60000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  return date(utc);
}

export function daysUntil(utc: string | null | undefined): number | null {
  const parsed = parseTimestamp(utc);
  if (!parsed) return null;
  return Math.ceil((parsed.getTime() - Date.now()) / 86400000);
}

// --- Listing vocabulary -----------------------------------------------------------------

/**
 * The single place that turns availability into words.
 *
 * When the API reports Unconfirmed it also withholds the number, so the UI says
 * "Availability unconfirmed" rather than implying beds are free right now.
 */
export function availabilityLabel(
  confidence: AvailabilityConfidence,
  units: number | null | undefined,
  unitLabel = 'beds',
): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  if (confidence === 'Unconfirmed' || units === null || units === undefined) {
    return { text: 'Availability unconfirmed', tone: 'warn' };
  }

  if (units <= 0) {
    return { text: `No ${unitLabel} available`, tone: 'muted' };
  }

  const noun = units === 1 ? unitLabel.replace(/s$/, '') : unitLabel;
  const suffix = confidence === 'OwnerReported' ? ' (owner reported)' : '';

  return { text: `${units} ${noun} available${suffix}`, tone: 'ok' };
}

export function statusLabel(status: ListingStatus): { text: string; variant: string } {
  switch (status) {
    case 'Draft':
      return { text: 'Draft', variant: 'secondary' };
    case 'PendingPayment':
      return { text: 'Payment due', variant: 'warning' };
    case 'PendingReview':
      return { text: 'In review', variant: 'info' };
    case 'Rejected':
      return { text: 'Rejected', variant: 'danger' };
    case 'Active':
      return { text: 'Live', variant: 'success' };
    case 'ExpiringSoon':
      return { text: 'Expiring soon', variant: 'warning' };
    case 'Expired':
      return { text: 'Expired', variant: 'danger' };
    case 'Suspended':
      return { text: 'Suspended', variant: 'danger' };
    case 'Archived':
      return { text: 'Archived', variant: 'secondary' };
    default:
      return { text: status, variant: 'secondary' };
  }
}

export function chargeFrequencyLabel(frequency: ChargeFrequency): string {
  switch (frequency) {
    case 'Monthly':
      return 'per month';
    case 'OneTime':
      return 'one time';
    case 'PerNight':
      return 'per night';
    case 'PerStay':
      return 'per stay';
    default:
      return frequency;
  }
}

/** Turns an enum name into readable words: "SemiFurnished" -> "Semi furnished". */
export function humanise(value: string | null | undefined): string {
  if (!value) return '';

  const spaced = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function sharingLabel(value: string): string {
  switch (value) {
    case 'Single':
      return 'Single occupancy';
    case 'TwoSharing':
      return '2 sharing';
    case 'ThreeSharing':
      return '3 sharing';
    case 'FourSharing':
      return '4 sharing';
    case 'FiveSharing':
      return '5 sharing';
    case 'SixSharing':
      return '6 sharing';
    case 'SevenSharing':
      return '7 sharing';
    case 'EightSharing':
      return '8 sharing';
    default:
      return 'Other sharing';
  }
}

export function categoryLabel(category: string): string {
  switch (category) {
    case 'PgHostel':
      return 'PG & Hostel';
    case 'HomeRental':
      return 'Home for rent';
    case 'Hotel':
      return 'Hotel & Short stay';
    default:
      return category;
  }
}

export function categoryPath(category: string): string {
  switch (category) {
    case 'PgHostel':
      return '/pg';
    case 'HomeRental':
      return '/homes';
    case 'Hotel':
      return '/hotels';
    default:
      return '/';
  }
}

export function distanceLabel(km: number | null | undefined): string | null {
  if (km === null || km === undefined) return null;

  return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;
}

/** Strips the number down to digits for a tel: or wa.me link. */
export function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

export function whatsAppLink(phone: string, message: string): string {
  return `https://wa.me/${phoneDigits(phone)}?text=${encodeURIComponent(message)}`;
}

export function directionsLink(latitude?: number, longitude?: number, address?: string): string {
  if (latitude !== undefined && longitude !== undefined) {
    return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address ?? '')}`;
}
